import React, { useEffect, useMemo, useState } from 'react'
import { xApp, type payloadEventData, type xAppEnvironment } from 'xumm-xapp-sdk'
import { XummLink } from './components/XummLink'

type ReadyState =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'ok' }
  | { state: 'error'; message: string }

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0
}

const XappLanding: React.FC = () => {
  const [environment, setEnvironment] = useState<xAppEnvironment>({ version: '', ott: '' })
  const [readyState, setReadyState] = useState<ReadyState>({ state: 'idle' })
  const [lastPayload, setLastPayload] = useState<payloadEventData | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)

  const detectUrl = useMemo(() => {
    const v = import.meta.env.VITE_XAMAN_XAPP_DETECT_URL
    return isNonEmptyString(v) ? v.trim() : ''
  }, [])

  const inXamanXapp = useMemo(() => isNonEmptyString(environment.ott), [environment.ott])

  useEffect(() => {
    const sdk = new xApp()

    // Best-effort: in xApp context, this will include version/ott from UA.
    setEnvironment(sdk.getEnvironment())

    const onPayload = (data: payloadEventData) => {
      setLastPayload(data)
    }

    sdk.on('payload', onPayload)

    setReadyState({ state: 'sending' })
    ;(async () => {
      try {
        const r = await sdk.ready()
        if (r instanceof Error) {
          setReadyState({ state: 'error', message: r.message })
        } else {
          setReadyState({ state: 'ok' })
        }
      } catch (e) {
        setReadyState({ state: 'error', message: (e as Error)?.message || String(e) })
      }
    })()

    return () => {
      sdk.off('payload', onPayload)
    }
  }, [])

  const closeXapp = async () => {
    setLastError(null)
    try {
      const sdk = new xApp()
      const r = await sdk.close()
      if (r instanceof Error) setLastError(r.message)
    } catch (e) {
      setLastError((e as Error)?.message || String(e))
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(900px 420px at 20% 10%, rgba(99,102,241,0.30), transparent 60%), radial-gradient(700px 420px at 85% 30%, rgba(16,185,129,0.22), transparent 60%), #0b1220',
        color: '#e5e7eb',
        padding: '24px'
      }}
    >
      <div style={{ maxWidth: '860px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#93c5fd' }}>
              Unykorn
            </div>
            <h1 style={{ margin: '8px 0 0', fontSize: '28px', lineHeight: 1.2 }}>Xaman xApp Entry</h1>
          </div>

          <XummLink
            href="https://unykorn.org/"
            style={{
              padding: '10px 12px',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(0,0,0,0.25)',
              color: '#e5e7eb',
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}
          >
            Back to site
          </XummLink>
        </div>

        <div
          style={{
            marginTop: '18px',
            padding: '16px',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(0,0,0,0.35)'
          }}
        >
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>Host status</div>

          <div style={{ fontSize: '14px', lineHeight: 1.8 }}>
            <div>
              <strong style={{ color: '#93c5fd' }}>In xApp:</strong> {inXamanXapp ? 'YES' : 'NO'}
            </div>
            <div>
              <strong style={{ color: '#93c5fd' }}>Xaman version:</strong> {environment.version || '(unknown)'}
            </div>
            <div>
              <strong style={{ color: '#93c5fd' }}>OTT:</strong>{' '}
              <span style={{ fontFamily: 'monospace' }}>{environment.ott || '(missing)'}</span>
            </div>
            <div>
              <strong style={{ color: '#93c5fd' }}>ready():</strong>{' '}
              {readyState.state === 'idle' && 'waiting'}
              {readyState.state === 'sending' && 'sending...'}
              {readyState.state === 'ok' && 'ok'}
              {readyState.state === 'error' && (
                <span style={{ color: '#fecaca' }}>error: {readyState.message}</span>
              )}
            </div>
          </div>

          {!inXamanXapp && (
            <div
              style={{
                marginTop: '14px',
                padding: '14px',
                borderRadius: '14px',
                background: 'rgba(251,191,36,0.10)',
                border: '1px solid rgba(251,191,36,0.25)',
                color: '#fde68a',
                fontSize: '13px',
                lineHeight: 1.6
              }}
            >
              This page is meant to be opened <strong>inside Xaman</strong> as an xApp.
              {detectUrl ? (
                <>
                  {' '}
                  Open using your Detect URL:{' '}
                  <XummLink href={detectUrl} style={{ color: '#ffffff', textDecoration: 'underline' }}>
                    {detectUrl}
                  </XummLink>
                </>
              ) : (
                <>
                  {' '}
                  Set <code style={{ fontFamily: 'monospace' }}>VITE_XAMAN_XAPP_DETECT_URL</code> to show a one-tap launch link.
                </>
              )}
            </div>
          )}

          {inXamanXapp && (
            <div style={{ marginTop: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={closeXapp}
                style={{
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: '#ffffff',
                  color: '#000000',
                  fontSize: '12px',
                  fontWeight: 900,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}
              >
                Close xApp
              </button>

              <XummLink
                href="https://unykorn.org/"
                style={{
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(0,0,0,0.25)',
                  color: '#e5e7eb',
                  textDecoration: 'none',
                  fontSize: '12px',
                  fontWeight: 900,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                Open full site
              </XummLink>
            </div>
          )}

          {lastError && (
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.22)',
                borderRadius: '12px',
                color: '#fecaca',
                fontSize: '13px'
              }}
            >
              {lastError}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '18px',
            padding: '16px',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(0,0,0,0.25)'
          }}
        >
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>Legal</div>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <XummLink href="https://unykorn.org/terms" style={{ color: '#60a5fa', fontSize: '13px', textDecoration: 'none' }}>Terms of Service</XummLink>
            <XummLink href="https://unykorn.org/privacy" style={{ color: '#60a5fa', fontSize: '13px', textDecoration: 'none' }}>Privacy Policy</XummLink>
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>Last xApp event</div>
          <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#e5e7eb' }}>
            {lastPayload ? (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  padding: '12px',
                  borderRadius: '14px',
                  background: 'rgba(0,0,0,0.35)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  margin: 0
                }}
              >
                {JSON.stringify(lastPayload, null, 2)}
              </pre>
            ) : (
              <div style={{ color: '#9ca3af' }}>No payload events received yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default XappLanding
